/**
 * Lexical Rich Text Editor Component
 * Full-featured editor with comprehensive toolbar
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeNode } from '@lexical/code';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $insertNodes } from 'lexical';

import ToolbarPlugin from './ToolbarPlugin';
import { ImageNode } from '../nodes/ImageNode';
import ImagesPlugin from '../plugins/ImagesPlugin';

// Editor theme
const theme = {
    ltr: 'ltr',
    rtl: 'rtl',
    paragraph: 'editor-paragraph',
    quote: 'editor-quote',
    heading: {
        h1: 'editor-heading-h1',
        h2: 'editor-heading-h2',
        h3: 'editor-heading-h3',
        h4: 'editor-heading-h4',
        h5: 'editor-heading-h5',
        h6: 'editor-heading-h6',
    },
    list: {
        nested: {
            listitem: 'editor-nested-listitem',
        },
        ol: 'editor-list-ol',
        ul: 'editor-list-ul',
        listitem: 'editor-listitem',
    },
    image: 'editor-image',
    link: 'editor-link',
    text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline',
        strikethrough: 'editor-text-strikethrough',
        code: 'editor-text-code',
    },
    code: 'editor-code',
    horizontalRule: 'editor-hr',
};

// Plugin to sync content with API
function SyncPlugin({ editorAPI, originalElement }) {
    const [editor] = useLexicalComposerContext();
    
    useEffect(() => {
        // Set editor reference in API
        editorAPI._editor = editor;
        
        // Update content handler
        editorAPI._updateContent = (action, data) => {
            editor.update(() => {
                if (action === 'setContents' && data.content) {
                    const root = $getRoot();
                    root.clear();
                    
                    if (typeof data.content === 'string' && data.content.includes('<')) {
                        // HTML content
                        const parser = new DOMParser();
                        const dom = parser.parseFromString(data.content, 'text/html');
                        const nodes = $generateNodesFromDOM(editor, dom);
                        $insertNodes(nodes);
                    }
                }
                if (action === 'insertEmbed' && data.type === 'image') {
                    // Image insertion handled by ImagesPlugin
                }
            });
        };
    }, [editor, editorAPI]);
    
    return null;
}

// Handle content changes
function OnChangeHandler({ editorAPI, originalElement }) {
    const [editor] = useLexicalComposerContext();
    
    const onChange = useCallback((editorState) => {
        editorState.read(() => {
            const html = $generateHtmlFromNodes(editor, null);
            editorAPI.root._innerHTML = html;
            
            // Sync to original form element
            if (originalElement && (originalElement.tagName === 'TEXTAREA' || originalElement.tagName === 'INPUT')) {
                originalElement.value = html;
            }
            
            // Emit change event for Quill compatibility
            editorAPI.emit('text-change');
        });
    }, [editor, editorAPI, originalElement]);
    
    return <OnChangePlugin onChange={onChange} />;
}

// Load initial content
function InitialContentPlugin({ initialContent }) {
    const [editor] = useLexicalComposerContext();
    const hasLoaded = useRef(false);
    
    useEffect(() => {
        if (hasLoaded.current || !initialContent) return;
        hasLoaded.current = true;
        
        editor.update(() => {
            if (initialContent.includes('<')) {
                // HTML content
                const parser = new DOMParser();
                const dom = parser.parseFromString(initialContent, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);
                const root = $getRoot();
                root.clear();
                $insertNodes(nodes);
            }
        });
    }, [editor, initialContent]);
    
    return null;
}

export default function LexicalEditor({ 
    selector, 
    initialContent, 
    editorAPI, 
    originalElement,
    placeholder = 'Start writing...' 
}) {
    const initialConfig = {
        namespace: `LexicalEditor-${selector}`,
        theme,
        onError: (error) => {
            console.error('Lexical Editor Error:', error);
        },
        nodes: [
            HeadingNode,
            QuoteNode,
            ListNode,
            ListItemNode,
            LinkNode,
            AutoLinkNode,
            CodeNode,
            HorizontalRuleNode,
            ImageNode,
        ],
    };

    return (
        <div className="lexical-editor-wrapper">
            <LexicalComposer initialConfig={initialConfig}>
                <ToolbarPlugin editorAPI={editorAPI} />
                <div className="lexical-editor-container">
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable className="lexical-content-editable" />
                        }
                        placeholder={
                            <div className="lexical-placeholder">{placeholder}</div>
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <ImagesPlugin />
                    <InitialContentPlugin initialContent={initialContent} />
                    <SyncPlugin editorAPI={editorAPI} originalElement={originalElement} />
                    <OnChangeHandler editorAPI={editorAPI} originalElement={originalElement} />
                </div>
            </LexicalComposer>
        </div>
    );
}
